import React, { useMemo } from 'react';
import type { RoutePath, RoutesPaths } from '@shared/core';
import { Redirect, Route, RoutesProvider, Switch, useRouterHelper } from '@shared/core';
import { LazyTyingTheKnot } from './routes/TyingTheKnot/TyingTheKnot.lazy';
import { useAutoRedirectToScenario } from './hooks/useAutoRedirectToScenario';
import { CreateWeddingLayout, WeddingFormTopBar } from './components/CreateWeddingLayout/CreateWeddingLayout';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { CreateWeddingGuards } from './components/CreateWeddingGuards/CreateWeddingGuards';
import { useRedirectAuthenticatedAway } from './components/CreateWeddingGuards/useRedirectAuthenticatedAway';
import { LazyFormFlowProviders } from './components/FormFlowProviders';
import { useCreateWeddingTelemetry } from './CreateWedding.telemetry';
import { LazyCreateAccount } from './routes/CreateAccount/CreateAccount.lazy';
import { LazySignIn } from './routes/SignIn/SignIn.lazy';
import { LazyCreateWeddingForm } from './routes/CreateWeddingForm/CreateWeddingForm.lazy';
import { LazyForgotPassword } from './routes/ForgotPassword/ForgotPassword.lazy';
import { LazySocialAccount } from './routes/SocialAccount/SocialAccount.lazy';
import { CreateWeddingFunnelLoader } from './components/CreateWeddingFunnelLoader/CreateWeddingFunnelLoader';

const CreateAccountRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'createAccount' });
  useRedirectAuthenticatedAway(routePaths.createWeddingForm.path);

  return (
    <CreateWeddingLayout>
      <LazyCreateAccount fallback={<CreateWeddingFunnelLoader />} />
    </CreateWeddingLayout>
  );
};

const SignInRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'signIn' });
  useRedirectAuthenticatedAway(routePaths.createWeddingForm.path);

  return (
    <CreateWeddingLayout>
      <LazySignIn fallback={<CreateWeddingFunnelLoader />} />
    </CreateWeddingLayout>
  );
};

const ForgotPasswordRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'forgotPassword' });
  useRedirectAuthenticatedAway(routePaths.createWeddingForm.path);

  return (
    <CreateWeddingLayout>
      <LazyForgotPassword fallback={<CreateWeddingFunnelLoader />} />
    </CreateWeddingLayout>
  );
};

const SocialAccountRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'socialAccount' });

  return (
    <CreateWeddingLayout>
      <CreateWeddingGuards authMode="allowOnlyAuthenticated">
        <LazySocialAccount fallback={<CreateWeddingFunnelLoader />} />
      </CreateWeddingGuards>
    </CreateWeddingLayout>
  );
};

const CreateWeddingFormRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'createWeddingForm' });
  useAutoRedirectToScenario();
  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <CreateWeddingLayout topPanel={isMobileOrTablet ? <WeddingFormTopBar /> : undefined}>
      <CreateWeddingGuards authMode="requireJoyOrNonSocialAuthenticated">
        <LazyCreateWeddingForm fallback={<CreateWeddingFunnelLoader />} />
      </CreateWeddingGuards>
    </CreateWeddingLayout>
  );
};

const TyingTheKnotRoute: React.FC = () => {
  useCreateWeddingTelemetry({ page: 'tyingTheKnot' });

  return (
    <CreateWeddingLayout>
      <LazyTyingTheKnot fallback={<CreateWeddingFunnelLoader />} />
    </CreateWeddingLayout>
  );
};

export type RoutesNames = 'createWeddingForm' | 'createAccount' | 'signIn' | 'forgotPassword' | 'socialAccount' | 'tyingTheKnot';

export const routePaths: RoutesPaths<RoutesNames> = {
  createWeddingForm: {
    path: '',
    goToPath: () => '',
  },
  createAccount: {
    path: 'create-account',
    goToPath: () => 'create-account',
  },
  signIn: {
    path: 'sign-in',
    goToPath: () => 'sign-in',
  },
  forgotPassword: {
    path: 'forgot-password',
    goToPath: () => 'forgot-password',
  },
  socialAccount: {
    path: 'social-account',
    goToPath: () => 'social-account',
  },
  tyingTheKnot: {
    path: 'tying-the-knot',
    goToPath: () => 'tying-the-knot',
  },
};

const RoutesCreateWedding = () => {
  const routeHelpers = useRouterHelper();

  const publicRoutes: Array<RoutePath<{}>> = useMemo(
    () => [
      {
        path: routeHelpers.buildPath(routePaths.createAccount.path),
        component: () => <CreateAccountRoute />,
      },
      {
        path: routeHelpers.buildPath(routePaths.signIn.path),
        component: () => <SignInRoute />,
      },
      {
        path: routeHelpers.buildPath(routePaths.forgotPassword.path),
        component: () => <ForgotPasswordRoute />,
      },
      {
        path: routeHelpers.buildPath(routePaths.socialAccount.path),
        component: () => <SocialAccountRoute />,
      },
    ],
    [routeHelpers],
  );

  const tyingTheKnotPath = routeHelpers.buildPath(routePaths.tyingTheKnot.path);
  const createWeddingFormPath = routeHelpers.buildPath(routePaths.createWeddingForm.path);
  const fallbackPath = createWeddingFormPath;

  return (
    <Switch>
      {publicRoutes.map(route => (
        <Route
          exact
          key={route.path}
          path={route.path}
          render={route.component}
        />
      ))}
      {/*
        Form + tying-the-knot share EventState (createEvent mutation result is
        read on the success page) and AllowedDesignParams. Wrapping them as a
        group keeps the providers mounted across the intra-group navigation.
        The lazy boundary keeps the GraphQL chunk out of the createwedding
        entry, so create-account/sign-in/forgot-password don't preload it.
      */}
      <Route path={[tyingTheKnotPath, createWeddingFormPath]}>
        <LazyFormFlowProviders fallback={<CreateWeddingFunnelLoader />}>
          <Switch>
            <Route
              exact
              path={tyingTheKnotPath}
              render={() => <TyingTheKnotRoute />}
            />
            <Route
              exact
              path={createWeddingFormPath}
              render={() => <CreateWeddingFormRoute />}
            />
            {/*
              The grouped parent Route is non-exact (it must match both inner
              paths), so it prefix-matches any /createwedding/* URL. Without
              this redirect, an unknown subpath would render a blank wrapper
              instead of falling through to the outer <Redirect>.
            */}
            <Redirect to={fallbackPath} />
          </Switch>
        </LazyFormFlowProviders>
      </Route>
      <Redirect to={fallbackPath} />
    </Switch>
  );
};

export const RouterCreateWedding: React.FC<{ appUrl: string }> = ({ appUrl }) => {
  return (
    <RoutesProvider appUrl={appUrl}>
      <RoutesCreateWedding />
    </RoutesProvider>
  );
};
